import React, { useState } from "react";
import { View } from "react-native";

import { useGameplayStore } from "../../game/stores/useGameplayStore";
import { AUTO_SPEEDS, type AutoGoal, type AutoSpeed } from "../../dev/autoPlay";
import { useDevSession } from "../../dev/devSession";
import { useAutoPlay, useShownBestNext } from "../../dev/hooks";
import { Btn, Note, Row, Toggle } from "./controls";

const GOALS: { goal: AutoGoal; label: string }[] = [
  { goal: "step", label: "Step best" },
  { goal: "oneBefore", label: "Play to one-before-win" },
  { goal: "win", label: "Play to win" },
  { goal: "detour", label: "Make a detour" },
];

/** Show the best move (view only) and auto-play through the tap handler. */
const PlaySection: React.FC<{ onMove?: (word: string) => void }> = ({
  onMove,
}) => {
  const showBestMove = useDevSession((s) => s.showBestMove);
  const speed = useDevSession((s) => s.speed);
  const playing = useGameplayStore((s) => s.gameStatus === "playing");
  const next = useShownBestNext();
  const { run, stop, running } = useAutoPlay(onMove);
  const [note, setNote] = useState<string | null>(null);

  const go = async (goal: AutoGoal) => {
    setNote(null);
    const moves = await run(goal);
    if (moves === 0) {
      setNote(
        goal === "detour" ? "No detour available here." : "Nothing to play.",
      );
    }
  };

  return (
    <View>
      <Toggle
        testID="dev-show-optimal"
        label="Show best move"
        value={showBestMove}
        onChange={(v) => {
          const dev = useDevSession.getState();
          if (v) dev.markUsed();
          dev.set({ showBestMove: v });
        }}
      />
      {showBestMove && <Note>next best move: {next ?? "—"}</Note>}
      <Row wrap>
        {GOALS.map((g) => (
          <Btn
            key={g.goal}
            testID={`dev-auto-${g.goal}`}
            label={g.label}
            disabled={!playing || running || !onMove}
            onPress={() => go(g.goal)}
          />
        ))}
        {running && <Btn testID="dev-auto-stop" label="Stop" onPress={stop} />}
      </Row>
      <Row wrap>
        <Note>Speed</Note>
        {(Object.keys(AUTO_SPEEDS) as AutoSpeed[]).map((k) => (
          <Btn
            key={k}
            label={`${k} ${AUTO_SPEEDS[k]}ms`}
            on={speed === k}
            onPress={() => useDevSession.getState().set({ speed: k })}
          />
        ))}
      </Row>
      {note && <Note>{note}</Note>}
    </View>
  );
};

export default PlaySection;
